import { useState } from 'react';
import { Link } from 'react-router-dom';
import { formatDateTime } from '../../lib/dateFmt';
import {
  ListChecks,
  Download,
  Users,
  UserX,
  PackageX,
  AlertCircle,
  CheckCircle2,
} from 'lucide-react';
import { useCurrentUser } from '../../hooks/useAuth';
import {
  useAttendanceReport,
  useNoShowsReport,
  useOutstandingReport,
} from '../../hooks/useReports';
import { Button } from '../../components/ui/Button';
import { PageHeader } from '../../components/ui/PageHeader';
import { Pill } from '../../components/ui/Pill';
import { SkeletonList } from '../../components/ui/Skeleton';
import { EmptyState } from '../../components/ui/EmptyState';

const ALLOWED = ['admin', 'coordinator', 'hr', 'super_admin'];

type Tab = 'attendance' | 'no_shows' | 'outstanding';

const TABS: Array<{ key: Tab; label: string; icon: typeof Users }> = [
  { key: 'attendance', label: 'Attendance', icon: Users },
  { key: 'no_shows', label: 'No-shows', icon: UserX },
  { key: 'outstanding', label: 'Outstanding', icon: PackageX },
];

export function Reports() {
  const { data: user, isLoading: meLoading } = useCurrentUser();
  const [tab, setTab] = useState<Tab>('attendance');
  const attendance = useAttendanceReport();
  const noShows = useNoShowsReport();
  const outstanding = useOutstandingReport();

  if (meLoading) return <main className="p-6 text-sm text-fg-muted">Loading...</main>;
  if (!user) {
    return (
      <main className="p-6 text-sm text-fg-muted">
        Please <Link to="/login" className="underline">sign in</Link>.
      </main>
    );
  }
  if (!user.roles.some((r) => ALLOWED.includes(r))) {
    return (
      <main className="p-6 text-sm text-fg-muted">
        Reports are restricted to admin / coordinator / HR / super-admin.
      </main>
    );
  }

  return (
    <main className="min-h-screen bg-bg text-fg">
      <div className="mx-auto max-w-5xl px-4 py-6 sm:px-6 sm:py-8">
        <PageHeader
          eyebrowIcon={ListChecks}
          eyebrow="Reports"
          title="Day-of reports"
          description="Live snapshots and downloadable exports."
          trailing={
            <a href="/api/reports/asset-register.csv" download>
              <Button
                variant="secondary"
                size="md"
                iconLeft={<Download className="h-4 w-4" />}
              >
                Asset register CSV
              </Button>
            </a>
          }
        />

        <div
          role="tablist"
          className="mt-6 inline-flex rounded-token-sm border border-line/15 bg-surface p-0.5 shadow-token-sm"
        >
          {TABS.map((t) => {
            const Icon = t.icon;
            return (
              <button
                key={t.key}
                type="button"
                role="tab"
                aria-selected={tab === t.key}
                onClick={() => setTab(t.key)}
                className={`inline-flex items-center gap-1.5 rounded-token-sm px-3 py-1.5 text-sm font-medium transition-colors ${
                  tab === t.key
                    ? 'bg-accent text-fg-on-accent shadow-token-sm'
                    : 'text-fg-muted hover:bg-surface-2'
                }`}
              >
                <Icon className="h-4 w-4" />
                {t.label}
              </button>
            );
          })}
        </div>

        <div className="mt-4 overflow-hidden rounded-token-lg border border-line/10 bg-surface shadow-token-sm">
          {tab === 'attendance' ? (
            <PersonList
              isLoading={attendance.isLoading}
              error={attendance.error}
              people={attendance.data ?? []}
              showArrivalTime
              empty={{
                title: 'Nobody has arrived yet',
                description: 'Once reception checks people in they show up here.',
              }}
            />
          ) : tab === 'no_shows' ? (
            <PersonList
              isLoading={noShows.isLoading}
              error={noShows.error}
              people={noShows.data ?? []}
              empty={{
                title: 'Everyone has arrived',
                description: 'No outstanding no-shows. Nice work.',
                icon: <CheckCircle2 className="h-6 w-6" />,
              }}
            />
          ) : (
            <OutstandingList
              isLoading={outstanding.isLoading}
              error={outstanding.error}
              data={outstanding.data ?? []}
            />
          )}
        </div>
      </div>
    </main>
  );
}

function PersonList({
  isLoading,
  error,
  people,
  empty,
  showArrivalTime,
}: {
  isLoading: boolean;
  error: unknown;
  people: import('../../hooks/usePeople').PersonApi[];
  empty: { title: string; description?: string; icon?: React.ReactNode };
  showArrivalTime?: boolean;
}) {
  if (isLoading) return <SkeletonList rows={6} />;
  if (error) {
    return (
      <EmptyState
        icon={<AlertCircle className="h-6 w-6" />}
        title="Couldn't load report"
        description={(error as Error).message}
      />
    );
  }
  if (people.length === 0) {
    return (
      <EmptyState
        icon={empty.icon ?? <Users className="h-6 w-6" />}
        title={empty.title}
        description={empty.description}
      />
    );
  }
  return (
    <div className="overflow-x-auto">
      <table className="w-full text-left text-sm">
        <thead className="bg-surface-2 text-xs uppercase tracking-wider text-fg-dim">
          <tr>
            <th className="px-4 py-2.5">Name</th>
            <th className="px-4 py-2.5">Position</th>
            <th className="px-4 py-2.5">Email</th>
            {showArrivalTime ? <th className="px-4 py-2.5">Arrived</th> : null}
          </tr>
        </thead>
        <tbody>
          {people.map((p) => (
            <tr key={p.id} className="border-t border-divider/10">
              <td className="px-4 py-2.5 font-medium text-fg">{p.full_name}</td>
              <td className="px-4 py-2.5 text-fg-muted">{p.position ?? '-'}</td>
              <td className="px-4 py-2.5 text-fg-muted">{p.email}</td>
              {showArrivalTime ? (
                <td className="px-4 py-2.5 text-fg-muted">
                  {p.arrived_at ? formatDateTime(p.arrived_at) : ''}
                </td>
              ) : null}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function OutstandingList({
  isLoading,
  error,
  data,
}: {
  isLoading: boolean;
  error: unknown;
  data: import('../../hooks/useReports').OutstandingForPerson[];
}) {
  if (isLoading) return <SkeletonList rows={4} height="h-24" />;
  if (error) {
    return (
      <EmptyState
        icon={<AlertCircle className="h-6 w-6" />}
        title="Couldn't load report"
        description={(error as Error).message}
      />
    );
  }
  if (data.length === 0) {
    return (
      <EmptyState
        icon={<CheckCircle2 className="h-6 w-6" />}
        title="Nothing outstanding"
        description="Every arrived attendee has received the items expected for their position."
      />
    );
  }
  return (
    <div className="divide-y divide-divider/10">
      {data.map((p) => (
        <div key={p.person.id} className="px-4 py-3">
          <div className="font-semibold text-fg">{p.person.full_name}</div>
          <div className="text-xs text-fg-dim">
            {p.person.position ?? 'Position unknown'} · {p.person.email}
          </div>
          <ul className="mt-2 grid gap-1 sm:grid-cols-2">
            {p.outstanding.map((it) => (
              <li
                key={`${it.station}-${it.name}`}
                className="flex items-baseline gap-2 text-sm"
              >
                <Pill tone="accent" size="sm">{it.station}</Pill>
                <span className="text-fg">
                  {it.name}
                  {it.outstanding_quantity > 1 ? (
                    <span className="text-fg-muted"> × {it.outstanding_quantity}</span>
                  ) : null}
                  {it.size_required ? (
                    <span className="text-warning-soft-fg"> (size needed)</span>
                  ) : null}
                </span>
              </li>
            ))}
          </ul>
        </div>
      ))}
    </div>
  );
}
